import { useEffect, useRef } from 'react'
import { getNotificationSettings } from '../services/profile-service'
import {
  assertConnectingTonePlaying,
  beginConnectingTonePlaythrough,
  markConnectingToneCompleted,
  markConnectingToneStarted,
  playRingtone,
  setNativeConnectingToneActive,
} from '../services/audio-manager'
import {
  addNativeConnectingToneCompleteListener,
  isNativeConnectingToneSupported,
  isNativeIncomingRingActive,
  playNativeConnectingTone,
  playNativeRingbackTone,
  stopNativeConnectingTone,
  waitForNativeDialReport,
} from '../services/voice-call/native-call'
import { getNativePlatform, isNativeApp } from '../utils/platform'
import { desiredCallSpeakerOn } from '../utils/call-audio-route'
import { callAudioRouteArbiter } from '../utils/call-audio-route-arbiter'
import { prepareNativeRingback } from '../services/voice-call/native-audio-route'
import { DIAL_TONE_URL } from '../utils/ringtone-preference'
import {
  clearActiveOutgoingDialPlan,
  getActiveOutgoingDialPlan,
  getCachedConnectingToneUrl,
  getSyncOutgoingDialPlan,
  prepareOutgoingDialPlan,
  resolveConnectingToneUrl,
  warmDialToneAssets,
} from '../features/dial-tones/outgoing-dial-plan'
import { refreshOrgDialToneConfig } from '../features/dial-tones/dial-tone-org-service'
import {
  noteIncomingRingStarted,
  noteIncomingRingSuppressed,
} from '../features/calls/utils/incoming-ring-timing'
import { recordCallFlow } from '../debug/call-flow-log'
import { useCallRingtone } from './use-call-ringtone'
import { useVoiceCallSession } from './use-voice-call-session'

/** Ringback safety cap when the dial plan has no hangup timeout. */
const DEFAULT_RINGBACK_CAP_MS = 90_000

/**
 * Re-assert cadence for the HTML5 connecting reminder (web / native fallback).
 * Bounded: assertConnectingTonePlaying never restarts a COMPLETED playthrough
 * (the reminder plays once, then stops — never loops).
 */
const CONNECTING_TONE_ASSERT_MS = 2_000

/**
 * Retry schedule for the native connecting reminder. AVAudioPlayer.play()
 * returns false when CallKit has not ACTIVATED the shared AVAudioSession yet —
 * on a CallKit answer the JS connecting phase starts ~100ms after the answer
 * action while `didActivate` lands ~300-400ms later (observed 2026-08-19).
 * A one-shot attempt therefore fails exactly on the path the native player
 * exists for, and the HTML5 fallback is inaudible under CallKit — so retry
 * across the activation window before ever falling back.
 */
const NATIVE_TONE_RETRY_DELAYS_MS = [400, 1200, 3000]

/**
 * Plays incoming/outgoing tones while a call is ringing, and the spoken
 * recorded-call reminder ("Remember, all calls are recorded") once as the call
 * connects. Split into PHASE-SCOPED effects with narrow deps on purpose: the
 * previous single effect re-ran on every `isSpeakerOn` change and each re-run
 * stopped + RESTARTED the tone (build 69/104). Tones restart only when the
 * call or phase actually changes.
 *
 * The reminder NEVER delays or blocks the connect (unlike the pre-#489 gate):
 * the call promotes to active on media as usual and the clip plays over the
 * first seconds of the call. Playback failures are logged, not fatal.
 */
export function useVoiceCallRingtone(): void {
  const session = useVoiceCallSession()
  const { start, stop } = useCallRingtone()
  const dialPlanCallIdRef = useRef<string | null>(null)
  const connectingUrlRef = useRef<string | null | undefined>(undefined)
  /** Latch: the call id whose reminder playthrough already began. */
  const disclosureCallIdRef = useRef<string | null>(null)

  // Pre-resolve org tone assets ONCE at mount so call-time playback starts
  // with the correct tone on the first tick instead of audibly force-swapping
  // from the built-in mid-ring.
  useEffect(() => {
    void warmDialToneAssets()
  }, [])

  // Native player self-stops after one playthrough. Record completion so the
  // once-per-call bookkeeping never restarts it (telemetry for device tests).
  useEffect(() => {
    if (!isNativeApp()) return
    let cancelled = false
    let unsub: () => void = () => {}
    void addNativeConnectingToneCompleteListener((durationMs) => {
      markConnectingToneCompleted()
      recordCallFlow('ctrl:connectingTone:completed', {
        durationMs,
        native: true,
        platform: getNativePlatform(),
      })
    }).then((fn) => {
      if (cancelled) {
        fn()
        return
      }
      unsub = fn
    })
    return () => {
      cancelled = true
      unsub()
    }
  }, [])

  const phase = session?.phase ?? null
  const callId = session?.callId ?? null
  const mediaType = session?.mediaType === 'video' ? 'video' : 'voice'
  const callPurpose = session?.callPurpose ?? null
  const osRingActive = session?.osRingActive === true
  const osAnswered = session?.osAnswered === true
  const transferHeld = Boolean(session?.transferRoom || session?.transfer?.onHold)
  /**
   * Answered: the post-answer window where the recorded-call reminder plays.
   * One boolean (not the raw phase) so the normal connecting → active promote
   * does NOT re-run the reminder effect mid-playthrough — the call connects
   * immediately and the clip keeps playing over the start of the call.
   */
  const answered = phase === 'connecting' || phase === 'active'

  // ── Session end / terminal phases: stop everything, clear per-call state ──
  // Keyed on callId/phase ONLY — mid-call session patches (roster, mute,
  // speaker) must not re-run this, or it would stop hold music / in-call audio
  // asserts on every store update. 'active' stays live so the reminder can
  // finish playing over the start of the call.
  useEffect(() => {
    if (
      callId &&
      (phase === 'incoming' || phase === 'outgoing' || phase === 'connecting' || phase === 'active')
    ) {
      return
    }
    stop()
    clearActiveOutgoingDialPlan()
    dialPlanCallIdRef.current = null
    connectingUrlRef.current = undefined
    if (!callId) disclosureCallIdRef.current = null
    if (isNativeApp()) {
      setNativeConnectingToneActive(false)
      void stopNativeConnectingTone()
    }
  }, [callId, phase, stop])

  // ── INCOMING ring ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!session || phase !== 'incoming' || !callId) return

    const priorityRing =
      callPurpose === 'urgent' || callPurpose === 'on-call' ? 'care-pulse' : null
    const isVideo = mediaType === 'video'
    const settings = getNotificationSettings()
    const ringtoneEnabled = isVideo
      ? settings.videoCallRingtone !== false
      : settings.voiceCallRingtone !== false
    if (osAnswered) {
      noteIncomingRingSuppressed(callId, 'os-answered')
      stop()
      return
    }
    // CallKit / ConnectionService owns the ring. `osRingActive` is in this
    // effect's deps, so the JS ringtone stands down the moment the OS ring
    // appears — including when the native surface comes up asynchronously
    // AFTER this effect already started ringing.
    if (isNativeApp() && (osRingActive || isNativeIncomingRingActive(callId))) {
      noteIncomingRingSuppressed(callId, 'os-ring')
      stop()
      return () => stop()
    }
    if (!ringtoneEnabled) {
      noteIncomingRingSuppressed(callId, 'ringtone-disabled')
      stop()
      return
    }

    let cancelled = false
    const ringNow = () => {
      if (cancelled) return
      noteIncomingRingStarted(callId)
      if (priorityRing) {
        playRingtone(priorityRing)
        return
      }
      // Callee: personal ringtone only (never org dial / ringback).
      start('incoming', true)
    }

    // WEB hidden tab: the dedicated background ring element
    // (web-call-notification) owns audible ringing while the tab is hidden.
    const isWeb = !isNativeApp() && typeof document !== 'undefined'
    const syncRingForVisibility = () => {
      if (cancelled) return
      if (isWeb && document.visibilityState === 'hidden') {
        stop()
        return
      }
      ringNow()
    }

    syncRingForVisibility()
    if (!priorityRing) {
      void refreshOrgDialToneConfig().then(() => {
        if (cancelled) return
        if (isWeb && document.visibilityState === 'hidden') return
        start('incoming', true, { force: true })
      })
    }
    if (isWeb) document.addEventListener('visibilitychange', syncRingForVisibility)
    return () => {
      cancelled = true
      if (isWeb) document.removeEventListener('visibilitychange', syncRingForVisibility)
      stop()
    }
  }, [session ? callId : null, phase, mediaType, callPurpose, osRingActive, osAnswered, start, stop]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── OUTGOING ringback ─────────────────────────────────────────────────────
  // NOT keyed on isSpeakerOn: the route is read live from the arbiter, and a
  // mid-ring speaker toggle must not restart the tone.
  useEffect(() => {
    if (!session || phase !== 'outgoing' || !callId) return

    let cancelled = false

    const startHtml5 = (playUrl: string | null, repeatCount: number, force: boolean) => {
      if (cancelled) return
      const ringbackSpeakerOn = desiredCallSpeakerOn()
      if (playUrl) {
        start('outgoing', ringbackSpeakerOn, {
          url: playUrl,
          repeatCount: repeatCount > 0 ? repeatCount : undefined,
          force,
        })
      } else {
        start('outgoing', ringbackSpeakerOn, force ? { force } : undefined)
      }
    }

    // NATIVE: play the ringback on the native player. The WebView's HTML5
    // ringback is paused by the in-call audio-focus grab / Telecom / CallKit
    // session ownership (build-104 silent dial tone). The native player rides
    // the voice or media path and is capped natively, so a suspended WebView
    // can never leave it ringing.
    if (isNativeApp()) {
      callAudioRouteArbiter.setPhase('outgoing-ringback')
      void (async () => {
        let plan = getSyncOutgoingDialPlan(mediaType, callId)
        if (!plan) {
          plan = await prepareOutgoingDialPlan(mediaType, { callId })
        }
        dialPlanCallIdRef.current = callId
        if (cancelled) return
        if (getNativePlatform() === 'android') {
          // Never a dial tone before Telecom has the call (it played on the
          // phone instead of the car).
          const report = await waitForNativeDialReport(callId)
          if (cancelled) return
          recordCallFlow('ctrl:ringback:dialReport', { result: report }, { callId })
        }
        await prepareNativeRingback(desiredCallSpeakerOn())
        if (cancelled) return
        const playUrl = plan.playUrl ?? DIAL_TONE_URL
        const maxDurationMs = plan.hangupTimeoutMs > 0 ? plan.hangupTimeoutMs : DEFAULT_RINGBACK_CAP_MS
        recordCallFlow(
          'ctrl:ringback:requested',
          { urlKind: playUrl.startsWith('http') ? 'remote' : 'asset', maxDurationMs, platform: getNativePlatform() },
          { callId },
        )
        const durationMs = await playNativeRingbackTone({
          url: playUrl,
          maxDurationMs,
          callId,
        })
        if (cancelled) {
          if (durationMs != null) void stopNativeConnectingTone('superseded')
          return
        }
        if (durationMs != null) {
          recordCallFlow(
            'ctrl:ringback:started',
            { durationMs, native: true, platform: getNativePlatform() },
            { callId },
          )
          // Native now owns the tone — stop any HTML5 ringback that slipped
          // in before ownership was pending (rekick racing this async chain).
          stop()
          return
        }
        // Older shell without playRingbackTone — HTML5 is all it has.
        recordCallFlow('ctrl:ringback:html5Fallback', { platform: getNativePlatform() }, { callId })
        startHtml5(plan.playUrl, plan.repeatCount, true)
      })()
      return () => {
        cancelled = true
        recordCallFlow('ctrl:ringback:stopped', { reason: 'phase-end', platform: getNativePlatform() }, { callId })
        void stopNativeConnectingTone('ringback-end')
        stop()
      }
    }

    // WEB: preferred path — warmed caches let us build the plan SYNCHRONOUSLY,
    // so the ORG tone starts on the very first tick (no built-in stopgap, no
    // audible mid-ring swap).
    const syncPlan = getSyncOutgoingDialPlan(mediaType, callId)
    if (syncPlan) {
      dialPlanCallIdRef.current = callId
      startHtml5(syncPlan.playUrl, syncPlan.repeatCount, false)
      return () => {
        cancelled = true
        stop()
      }
    }

    // Cold caches: start the BUILT-IN ringback IMMEDIATELY, org tone swaps in.
    startHtml5(null, 0, false)
    void (async () => {
      if (dialPlanCallIdRef.current !== callId) {
        await prepareOutgoingDialPlan(mediaType, { callId })
        dialPlanCallIdRef.current = callId
      }
      if (cancelled || dialPlanCallIdRef.current !== callId) return
      const plan = getActiveOutgoingDialPlan()
      if (plan?.playUrl) startHtml5(plan.playUrl, plan.repeatCount, true)
    })()

    return () => {
      cancelled = true
      stop()
    }
  }, [session ? callId : null, phase, mediaType, start, stop]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── RECORDED-CALL REMINDER (post-answer) ──────────────────────────────────
  // ONE playthrough per call, then stop (never loops). Plays for BOTH sides as
  // the call connects — an extra audible reminder on top of the durable
  // pre-dial consent (useful where the recording UI can't show, e.g. CarPlay).
  // Keyed on callId/answered/transfer only; the connecting → active promote and
  // other session changes (speaker toggle, mute, roster) MUST NOT re-run this
  // effect — a re-run used to stop + restart the native player and replay the
  // clip. It never blocks the connect: failures log and the call carries on.
  useEffect(() => {
    if (!session || !answered || !callId) return

    if (transferHeld) {
      stop()
      if (isNativeApp()) {
        setNativeConnectingToneActive(false)
        void stopNativeConnectingTone()
      }
      return () => stop()
    }

    // Reminder already started for this call (effect re-run, transfer
    // roundtrip, phase flap): never start a second playthrough.
    if (disclosureCallIdRef.current === callId) return
    disclosureCallIdRef.current = callId

    // Reset the playthrough clock and mark "awaiting start" so the once-only
    // bookkeeping survives native retries.
    beginConnectingTonePlaythrough()

    // Prefer the warmed org connecting clip so the FIRST start already uses
    // the right asset (no built-in → org force-swap mid-clip).
    if (connectingUrlRef.current === undefined) {
      connectingUrlRef.current = getCachedConnectingToneUrl() ?? undefined
    }

    let cancelled = false

    // Native (iOS CallKit / Android Telecom): HTML5 audio is silenced or
    // autoplay-blocked while the OS owns the voice session. Play through the
    // native player (AVAudioPlayer / MediaPlayer); it plays ONE playthrough
    // and stops itself — even if this WebView is suspended before the stop.
    if (isNativeApp()) {
      stop()
      setNativeConnectingToneActive(true)
      let retryAttempt = 0
      let retryTimer: ReturnType<typeof setTimeout> | null = null
      let assertTimer: ReturnType<typeof setInterval> | null = null
      const startHtml5Fallback = (url: string | null) => {
        recordCallFlow('ctrl:connectingTone:html5Fallback', {
          attempts: retryAttempt,
          platform: getNativePlatform(),
        })
        setNativeConnectingToneActive(false)
        start('connecting', desiredCallSpeakerOn(), { url: url ?? undefined, force: true })
        if (!assertTimer) {
          assertTimer = setInterval(() => {
            if (cancelled) return
            // Bounded internally: never restarts a completed playthrough.
            assertConnectingTonePlaying(connectingUrlRef.current ?? undefined)
          }, CONNECTING_TONE_ASSERT_MS)
        }
      }
      const startNativeTone = (url: string | null) => {
        // A retry may follow an HTML5 fallback that cleared the flag —
        // re-assert native ownership before each attempt so the HTML5
        // element stands down again the moment native playback works.
        setNativeConnectingToneActive(true)
        void playNativeConnectingTone(url, callId).then((durationMs) => {
          if (cancelled) {
            void stopNativeConnectingTone()
            return
          }
          if (durationMs != null) {
            markConnectingToneStarted(durationMs)
            recordCallFlow('ctrl:connectingTone:started', {
              durationMs,
              native: true,
              platform: getNativePlatform(),
            })
            return
          }
          // Older shells without the plugin method: don't burn the retry
          // window — HTML5 is the only path those builds have.
          if (!isNativeConnectingToneSupported()) {
            startHtml5Fallback(url)
            return
          }
          // play() commonly fails when attempted BEFORE CallKit/Telecom
          // activates the audio session (see NATIVE_TONE_RETRY_DELAYS_MS).
          const delayMs = NATIVE_TONE_RETRY_DELAYS_MS[retryAttempt]
          if (delayMs !== undefined) {
            retryAttempt += 1
            recordCallFlow('ctrl:connectingTone:nativeRetry', {
              attempt: retryAttempt,
              delayMs,
              platform: getNativePlatform(),
            })
            retryTimer = setTimeout(() => {
              retryTimer = null
              if (cancelled) return
              startNativeTone(connectingUrlRef.current ?? null)
            }, delayMs)
            return
          }
          startHtml5Fallback(url)
        })
      }
      startNativeTone(connectingUrlRef.current ?? null)
      void resolveConnectingToneUrl().then((url) => {
        if (cancelled) return
        // Restart ONLY when the built-in tone was playing (cold cache): the
        // spoken org clip must replace the synth tone, but an org clip that
        // already started must never replay just because the resolve returned
        // a fresh signed URL for the same asset (double reminder). Pending
        // retries pick up the refreshed URL from connectingUrlRef.
        const hadOrgUrl = connectingUrlRef.current != null
        connectingUrlRef.current = url ?? null
        if (url && !hadOrgUrl) {
          retryAttempt = 0
          startNativeTone(url)
        }
      })
      return () => {
        cancelled = true
        if (retryTimer) clearTimeout(retryTimer)
        if (assertTimer) clearInterval(assertTimer)
        setNativeConnectingToneActive(false)
        void stopNativeConnectingTone()
        stop()
      }
    }

    // Web: HTML5 reminder (one playthrough — playConnectingTone never loops).
    // Start immediately from the warmed cache (or built-in).
    start('connecting', desiredCallSpeakerOn(), {
      url: connectingUrlRef.current ?? undefined,
      force: connectingUrlRef.current !== undefined,
    })

    void resolveConnectingToneUrl().then((url) => {
      if (cancelled) return
      // Same rule as the native path: swap only built-in → org clip (cold
      // cache); never restart an org clip that is already playing.
      const hadOrgUrl = connectingUrlRef.current != null
      connectingUrlRef.current = url ?? null
      if (url && !hadOrgUrl) {
        start('connecting', desiredCallSpeakerOn(), { url, force: true })
      }
    })

    return () => {
      cancelled = true
      stop()
    }
  }, [session ? callId : null, answered, transferHeld, start, stop]) // eslint-disable-line react-hooks/exhaustive-deps
}
